import { useEffect, useRef, useState } from 'react'
import { DIRECT_PAY_COMPLETED_EVENT, DIRECT_PAY_FAILED_EVENT } from '../../../../analytics/events'
import { useAnalytics } from '../../../../context/useAnalytics'
import { logger } from '../../../app/logger'
import { useAllowanceContext } from './allowance/AllowanceProvider'
import { TransactionStatusEnum } from './constants'
import { useDirectPayContext } from './context/DirectPayContext'
import { walletErrorMessage } from './errors'
import { usePay } from './payment/usePay'

export type ActionLogItem = {
    text: string
    isSuccess?: boolean
    isError?: boolean
    isRetriable?: boolean
}

export const useFundDirectPayment = () => {
    const { refreshAllowance, currentAllowance } = useAllowanceContext()
    const { dispatch, selectedToken: tokenInfo, type } = useDirectPayContext()
    const { trackAnalyticsEvent } = useAnalytics()
    const [paying, setPaying] = useState<boolean>(false)
    const { pay, payState, method } = usePay(tokenInfo)
    const [actionLog, setActionLog] = useState<ActionLogItem | null>(null)
    const receipt = payState?.receipt
    const allowanceCheck = useRef<string | undefined>(undefined)
    const prevStatus = useRef<TransactionStatusEnum | undefined>(undefined)

    useEffect(() => {
        if (currentAllowance?.toString() == allowanceCheck.current) return
        allowanceCheck.current = currentAllowance?.toString()
        setActionLog(null)
    }, [currentAllowance])

    useEffect(() => {
        if (!payState) return
        if (payState.status === prevStatus.current) return
        prevStatus.current = payState.status as TransactionStatusEnum

        switch (payState.status as TransactionStatusEnum) {
            case TransactionStatusEnum.PendingSignature:
                setActionLog({
                    text: 'Confirm the transaction in your connected wallet...',
                    isSuccess: true,
                })
                break
            case TransactionStatusEnum.Mining:
                setActionLog({
                    text: 'Please wait, transaction is being processed...',
                    isSuccess: true,
                })
                break
            case TransactionStatusEnum.Success:
                setActionLog({
                    text: 'Transaction Confirmed!',
                    isSuccess: true,
                })
                setPaying(false)
                dispatch({ type: 'set-summary', payload: receipt ?? null })
                refreshAllowance()
                break
            case TransactionStatusEnum.Exception:
            case TransactionStatusEnum.Fail:
                logger.error(`Payment failed: ${payState.errorMessage}`)
                setActionLog({
                    text: walletErrorMessage({
                        message: payState.errorMessage,
                        symbol: tokenInfo?.symbol,
                        method,
                    }),
                    isError: true,
                    isRetriable: true,
                })
                trackAnalyticsEvent(DIRECT_PAY_FAILED_EVENT, {
                    token: tokenInfo?.symbol,
                    error: 'Transaction error',
                    type,
                })
                setPaying(false)
                break
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [payState])

    const fundPayment = async (amount: number, directPaymentId: string, currentTime: Date) => {
        try {
            setPaying(true)
            await pay(amount, directPaymentId, currentTime, setActionLog)
            trackAnalyticsEvent(DIRECT_PAY_COMPLETED_EVENT, {
                amount: amount,
                token: tokenInfo?.symbol,
                type,
            })
            return true
        } catch (err) {
            logger.exception(err as Error)
            setActionLog({
                text: `Error preparing transaction. Please try again`,
                isError: true,
            })
            trackAnalyticsEvent(DIRECT_PAY_FAILED_EVENT, {
                amount: amount,
                token: tokenInfo?.symbol,
                error: 'Preparation error',
                type,
            })
            return false
        } finally {
            setPaying(false)
        }
    }

    return {
        fundPayment,
        actionLog,
        paying,
        payState,
    }
}
